// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  averageValues,
  extentFromValues,
} from "../core/math.js";
import {
  cylinderKeys,
} from "../core/schema.js";
import {
  ENGINE_LABEL_STYLES,
  engineDescriptorsForData,
  engineScopedChannelLabel,
} from "../core/engines.js";
import {
  clampScore,
  engineRunningTrendRows,
  finiteValues,
  median,
  peerAverage,
  rankedQuantile as quantile,
  thermalEngineWorkPoint as engineWorkPoint,
} from "./utils.js";
import {
  turboParameters,
} from "./detectors/turbo.js";

function pointWithMaxValue(points, key) {
  return points.reduce((best, point) => {
    if (!Number.isFinite(point[key])) return best;
    if (!best || point[key] > best[key]) return point;
    return best;
  }, null);
}

function turboProfileValue(profile, key) {
  const turbo = profile?.turbo ?? {};
  const limits = profile?.operatingLimits ?? {};
  return turbo[key] ?? limits[key] ?? null;
}

function turboProfileValueAny(profile, keys) {
  for (const key of keys) {
    const value = turboProfileValue(profile, key);
    if (Number.isFinite(value)) return value;
  }
  return null;
}

function profileImpliesTurbo(profile) {
  if (profile?.turbo?.isTurbo === true || profile?.isTurbo === true) return true;
  const aspiration = String(profile?.engine?.aspiration ?? profile?.turbo?.turboType ?? profile?.turboType ?? "").toLowerCase();
  return /\bturbo/.test(aspiration);
}

function profileExplicitlyDisablesTurbo(profile) {
  if (profile?.turbo?.isTurbo === false) return true;
  if (profile?.turbo?.isTurbo === true || profile?.isTurbo === true) return false;
  if (profile?.isTurbo === false) return true;
  const aspiration = String(profile?.engine?.aspiration ?? profile?.turbo?.turboType ?? profile?.turboType ?? "").trim().toLowerCase();
  return Boolean(aspiration) && !/\bturbo|super|boost/.test(aspiration);
}

function turboDataKeys() {
  return [...turboParameters.telemetryKeys];
}

function turboMapValues(data) {
  return data.flatMap((point) => turboParameters.mapKeys.map((key) => point[key])).filter(Number.isFinite);
}

function turboPeakMapPoint(data) {
  return data.reduce((best, point) => {
    const value = Math.max(...turboParameters.mapKeys.map((key) => point[key]).filter(Number.isFinite));
    if (!Number.isFinite(value)) return best;
    const bestValue = best
      ? Math.max(...turboParameters.mapKeys.map((key) => best[key]).filter(Number.isFinite))
      : null;
    return !Number.isFinite(bestValue) || value > bestValue ? point : best;
  }, null);
}

function turboMapValueForPoint(point) {
  const values = turboParameters.mapKeys.map((key) => point?.[key]).filter(Number.isFinite);
  return values.length ? Math.max(...values) : null;
}

function hasTurboTelemetry(data) {
  return turboDataKeys().some((key) => data.some((point) => Number.isFinite(point[key])));
}

export function turboTemperatureValues(data) {
  const values = [];
  data.forEach((point) => {
    const tit = Number.isFinite(point.tit) ? point.tit : averageValues([point.tit1, point.tit2]);
    if (Number.isFinite(tit)) values.push(tit);
  });
  return values;
}

function turboPeakPoint(data, key) {
  if (key === "tit") {
    return data.reduce((best, point) => {
      const value = Number.isFinite(point.tit) ? point.tit : averageValues([point.tit1, point.tit2]);
      if (!Number.isFinite(value)) return best;
      const bestValue = best ? Number.isFinite(best.tit) ? best.tit : averageValues([best.tit1, best.tit2]) : null;
      return !Number.isFinite(bestValue) || value > bestValue ? point : best;
    }, null);
  }
  if (key === "map") return turboPeakMapPoint(data);
  return pointWithMaxValue(data, key);
}

function turboLimitWindows(data, key, limitForPoint, labelForWindow) {
  const resolveLimit = typeof limitForPoint === "function"
    ? limitForPoint
    : () => limitForPoint;
  const valueForPoint = (point) => (
    key === "tit"
      ? (Number.isFinite(point.tit) ? point.tit : averageValues([point.tit1, point.tit2]))
      : key === "map"
        ? turboMapValueForPoint(point)
      : point[key]
  );
  const rows = data.map((point, index) => {
    const limit = resolveLimit(point, index);
    const value = valueForPoint(point);
    return {
      index,
      point,
      value,
      limit,
      flagged: Number.isFinite(value) && Number.isFinite(limit) && value > limit,
    };
  });
  const windows = [];
  let startIndex = -1;
  const finishRun = (endIndex) => {
    if (startIndex < 0 || endIndex < startIndex) return;
    const run = rows.slice(startIndex, endIndex + 1).filter((row) => row.flagged);
    if (!run.length) return;
    const peak = run.reduce((best, row) => {
      if (!best || row.value - row.limit > best.value - best.limit) return row;
      return best;
    }, null);
    const first = rows[startIndex];
    const last = rows[endIndex];
    const startTime = first?.point?.elapsedSeconds ?? startIndex;
    const endTime = last?.point?.elapsedSeconds ?? endIndex;
    const duration = Number.isFinite(startTime) && Number.isFinite(endTime)
      ? Math.max(0, endTime - startTime)
      : Math.max(0, endIndex - startIndex);
    const label = typeof labelForWindow === "function"
      ? labelForWindow({ key, peak, first, last, duration, sampleCount: run.length })
      : labelForWindow;
    windows.push({
      startTime,
      endTime,
      duration,
      sampleCount: run.length,
      label,
      key,
      limit: peak?.limit ?? first?.limit ?? null,
      peakValue: peak?.value ?? null,
      peakTime: peak?.point?.elapsedSeconds ?? null,
      peakPhase: peak?.point?.phase ?? null,
      phase: first?.point?.phase ?? null,
      rpm: peak?.point?.rpm ?? null,
      fuelFlow: peak?.point?.fuelFlow ?? null,
      altitude: peak?.point?.altitude ?? null,
      oat: peak?.point?.oat ?? null,
      overshoot: Number.isFinite(peak?.value) && Number.isFinite(peak?.limit) ? peak.value - peak.limit : null,
    });
  };

  rows.forEach((row, index) => {
    if (row.flagged) {
      if (startIndex < 0) startIndex = index;
      return;
    }
    if (startIndex >= 0) finishRun(index - 1);
    startIndex = -1;
  });
  if (startIndex >= 0) finishRun(rows.length - 1);
  return windows;
}

function maxChtForPoint(point) {
  return extentFromValues(cylinderKeys("cht").map((key) => point?.[key]))?.max ?? null;
}

function titValueForPoint(point) {
  return Number.isFinite(point?.tit) ? point.tit : averageValues([point?.tit1, point?.tit2]);
}

function firstFinite(values) {
  return values.find(Number.isFinite) ?? null;
}

function lastFinite(values) {
  for (let index = values.length - 1; index >= 0; index -= 1) {
    if (Number.isFinite(values[index])) return values[index];
  }
  return null;
}

function fuelFlowMinimumForPhase(profile, phase) {
  const normalized = String(phase ?? "").toLowerCase();
  if (normalized === "takeoff") {
    const explicit = turboProfileValue(profile, "minTakeoffFuelFlowGph");
    if (Number.isFinite(explicit)) return explicit;
    const expected = turboProfileValue(profile, "takeoffFuelFlowGph");
    return Number.isFinite(expected) ? expected * turboParameters.highPowerHeat.fuelFlowToleranceRatio : null;
  }
  if (normalized === "climb") {
    const explicit = turboProfileValue(profile, "minClimbFuelFlowGph");
    if (Number.isFinite(explicit)) return explicit;
    const expected = turboProfileValue(profile, "climbFuelFlowGph");
    return Number.isFinite(expected) ? expected * turboParameters.highPowerHeat.fuelFlowToleranceRatio : null;
  }
  const explicit = turboProfileValueAny(profile, ["minHighPowerFuelFlowGph", "minCruiseFuelFlowGph"]);
  if (Number.isFinite(explicit)) return explicit;
  const expected = turboProfileValueAny(profile, ["highPowerFuelFlowGph", "cruiseFuelFlowGph"]);
  return Number.isFinite(expected) ? expected * turboParameters.highPowerHeat.fuelFlowToleranceRatio : null;
}

function dominantPhase(rows) {
  const counts = new Map();
  rows.forEach((row) => {
    const phase = row.point?.phase ?? "Unknown";
    counts.set(phase, (counts.get(phase) ?? 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

function summarizeHighPowerRun(rows, { profile, limits, peakMap, mapLimitForPoint }) {
  const config = turboParameters.highPowerHeat;
  if (!rows.length) return null;
  const first = rows[0];
  const last = rows.at(-1);
  const startTime = first.point?.elapsedSeconds ?? first.index;
  const endTime = last.point?.elapsedSeconds ?? last.index;
  const duration = Number.isFinite(startTime) && Number.isFinite(endTime)
    ? Math.max(0, endTime - startTime)
    : Math.max(0, last.index - first.index);
  if (duration < config.minWindowSeconds) return null;

  const points = rows.map((row) => row.point);
  const phase = dominantPhase(rows);
  const mapValues = points.map(turboMapValueForPoint).filter(Number.isFinite);
  const rpmValues = points.map((point) => point.rpm).filter(Number.isFinite);
  const fuelFlowValues = points.map((point) => point.fuelFlow).filter(Number.isFinite);
  const titValues = points.map(titValueForPoint).filter(Number.isFinite);
  const chtValues = points.map(maxChtForPoint).filter(Number.isFinite);
  const fuelFlowMinimums = points.map((point) => fuelFlowMinimumForPhase(profile, point.phase)).filter(Number.isFinite);
  const peakTit = extentFromValues(titValues)?.max ?? null;
  const peakCht = extentFromValues(chtValues)?.max ?? null;
  const startTit = firstFinite(points.map(titValueForPoint));
  const endTit = lastFinite(points.map(titValueForPoint));
  const startCht = firstFinite(points.map(maxChtForPoint));
  const endCht = lastFinite(points.map(maxChtForPoint));
  const titRise = Number.isFinite(startTit) && Number.isFinite(endTit) ? endTit - startTit : null;
  const chtRise = Number.isFinite(startCht) && Number.isFinite(endCht) ? endCht - startCht : null;
  const fuelFlowP10 = quantile(fuelFlowValues, 0.1);
  const fuelFlowMedian = median(fuelFlowValues);
  const expectedFuelFlowMin = median(fuelFlowMinimums);
  const fuelFlowShortfall = Number.isFinite(expectedFuelFlowMin) && Number.isFinite(fuelFlowP10)
    ? Math.max(0, expectedFuelFlowMin - fuelFlowP10)
    : null;
  const configuredFuelFlow = Number.isFinite(expectedFuelFlowMin);
  const lowFuelFlow = configuredFuelFlow
    && fuelFlowShortfall >= Math.max(config.fuelFlowShortfallFloorGph, config.fuelFlowShortfallReviewGph);
  const titMargin = Number.isFinite(limits.recommendedTitMarginF)
    ? limits.recommendedTitMarginF
    : config.defaultTitMarginF;
  const titWatchThreshold = Number.isFinite(limits.maxTitF)
    ? limits.maxTitF - titMargin
    : null;
  const chtLimit = turboProfileValue(profile, "chtMax");
  const chtWatchThreshold = Number.isFinite(chtLimit)
    ? Math.min(config.chtReviewF, Math.max(0, chtLimit - config.chtLimitMarginF))
    : config.chtReviewF;
  const highTitPressure = Number.isFinite(peakTit) && Number.isFinite(titWatchThreshold) && peakTit >= titWatchThreshold;
  const highChtPressure = Number.isFinite(peakCht) && peakCht >= chtWatchThreshold;
  const risingTit = Number.isFinite(titRise) && titRise >= config.titRiseReviewF;
  const risingCht = Number.isFinite(chtRise) && chtRise >= config.chtRiseReviewF;
  const heatCorroborated = highTitPressure || highChtPressure || risingTit || risingCht;
  const fallbackHeatWatch = !configuredFuelFlow
    && duration >= config.fallbackWindowSeconds
    && heatCorroborated
    && (highTitPressure || highChtPressure);
  if (!((lowFuelFlow && heatCorroborated) || fallbackHeatWatch)) return null;

  const maxMap = extentFromValues(mapValues)?.max ?? null;
  const averageRpm = averageValues(rpmValues);
  const averageFuelFlow = averageValues(fuelFlowValues);
  const peakMapPoint = rows.reduce((best, row) => {
    if (!Number.isFinite(row.map)) return best;
    if (!best || row.map > best.map) return row;
    return best;
  }, null);
  const heatRise = Math.max(
    0,
    Number.isFinite(titRise) ? titRise : 0,
    Number.isFinite(chtRise) ? chtRise : 0,
  );
  const basePenalty = lowFuelFlow ? config.penaltyBase : Math.max(2, config.penaltyBase / 2);
  const penalty = Math.min(
    lowFuelFlow ? config.penaltyCap : config.fallbackPenaltyCap,
    basePenalty
      + (Number.isFinite(fuelFlowShortfall) ? fuelFlowShortfall * config.fuelShortfallPenaltyWeight : 0)
      + heatRise * config.heatRisePenaltyWeight
      + duration / config.durationPenaltyDivisor,
  );
  const status = lowFuelFlow ? "fuel-flow-heat-review" : "heat-margin-watch";
  return {
    status,
    penalty,
    window: {
      kind: "highPowerHeat",
      key: "highPowerHeat",
      label: lowFuelFlow ? "High-power heat with low fuel flow" : "High-power turbo heat margin watch",
      startTime,
      endTime,
      duration,
      sampleCount: rows.length,
      phase,
      peakPhase: phase,
      map: maxMap,
      mapRatio: Number.isFinite(maxMap) && Number.isFinite(peakMapPoint?.mapLimit)
        ? maxMap / peakMapPoint.mapLimit
        : Number.isFinite(maxMap) && Number.isFinite(peakMap) && peakMap > 0
          ? maxMap / peakMap
          : null,
      mapLimit: peakMapPoint?.mapLimit ?? null,
      rpm: averageRpm,
      fuelFlow: averageFuelFlow,
      fuelFlowP10,
      expectedFuelFlowMin,
      fuelFlowShortfall,
      peakTit,
      peakCht,
      titRise,
      chtRise,
      highTitPressure,
      highChtPressure,
      risingTit,
      risingCht,
      altitude: averageValues(points.map((point) => point.altitude).filter(Number.isFinite)),
      oat: averageValues(points.map((point) => point.oat).filter(Number.isFinite)),
    },
  };
}

function summarizeHighPowerHeat(data, { profile, limits, peakMap, mapLimitForPoint }) {
  const config = turboParameters.highPowerHeat;
  const rows = data.map((point, index) => {
    const map = turboMapValueForPoint(point);
    const mapLimit = mapLimitForPoint(point);
    const phase = String(point?.phase ?? "");
    const requiredMapRatio = phase === "Cruise" ? config.cruiseMapRatio : config.minMapRatio;
    const mapRatio = Number.isFinite(map) && Number.isFinite(mapLimit) && mapLimit > 0
      ? map / mapLimit
      : Number.isFinite(map) && Number.isFinite(peakMap) && peakMap > 0
        ? map / peakMap
        : null;
    const highMap = Number.isFinite(mapRatio) && mapRatio >= requiredMapRatio;
    const highRpm = Number.isFinite(point?.rpm) && point.rpm >= config.minRpm;
    const reviewPhase = ["Takeoff", "Climb", "Cruise"].includes(phase);
    return {
      index,
      point,
      map,
      mapLimit,
      flagged: reviewPhase && highMap && highRpm,
    };
  });
  const candidates = [];
  let runStart = -1;
  const finishRun = (endIndex) => {
    if (runStart < 0 || endIndex < runStart) return;
    const run = rows.slice(runStart, endIndex + 1).filter((row) => row.flagged);
    const summary = summarizeHighPowerRun(run, { profile, limits, peakMap, mapLimitForPoint });
    if (summary) candidates.push(summary);
  };
  rows.forEach((row, index) => {
    if (row.flagged) {
      if (runStart < 0) runStart = index;
      return;
    }
    if (runStart >= 0) finishRun(index - 1);
    runStart = -1;
  });
  if (runStart >= 0) finishRun(rows.length - 1);
  return candidates.sort((a, b) => (b.penalty ?? 0) - (a.penalty ?? 0) || (b.window?.duration ?? 0) - (a.window?.duration ?? 0))[0] ?? null;
}

function turboContextPoint(data) {
  return turboPeakPoint(data, "tit")
    ?? turboPeakPoint(data, "map")
    ?? turboPeakPoint(data, "upperDeckPressure")
    ?? data.find((point) => turboDataKeys().some((key) => Number.isFinite(point[key])))
    ?? null;
}

function sensorChannelType(key) {
  if (String(key).startsWith("tit")) return "tit";
  return String(key).startsWith("egt") ? "egt" : "cht";
}

function scalarThermalProbeKeys(data) {
  const keys = [];
  const hasTit = data.some((point) => Number.isFinite(point.tit));
  const hasTit1 = data.some((point) => Number.isFinite(point.tit1));
  const hasTit2 = data.some((point) => Number.isFinite(point.tit2));
  if (hasTit1 || hasTit2) {
    if (hasTit1) keys.push("tit1");
    if (hasTit2) keys.push("tit2");
  } else if (hasTit) {
    keys.push("tit");
  }
  return keys;
}

function sensorPeerKeysForKey(key, allKeys = []) {
  const type = sensorChannelType(key);
  if (type === "tit") return allKeys.filter((candidate) => candidate !== key && sensorChannelType(candidate) === "tit");
  return cylinderKeys(type);
}

function sensorRangeForType(type) {
  const ranges = turboParameters.titQuality.ranges;
  const normalize = (range) => ({
    min: range.minF,
    max: range.maxF,
    coldPeerDelta: range.coldPeerDeltaF,
    hotPeerDelta: range.hotPeerDeltaF,
    peerMinimum: range.peerMinimumF,
    jumpFloor: range.jumpFloorF,
    jumpRate: range.jumpRateFps,
    flatlineSeconds: range.flatlineSeconds,
    flatlinePeerMotion: range.flatlinePeerMotionF,
    conditionSeconds: range.conditionSeconds,
    conditionGapSeconds: range.conditionGapSeconds,
    jumpWindowMinimum: range.jumpWindowMinimum,
    conditionAffectedRatio: range.conditionAffectedRatio,
  });
  if (type === "tit") {
    return normalize(ranges.tit);
  }
  return normalize(type === "egt" ? ranges.egt : ranges.cht);
}

function findSensorFlatline(rows, key, peerKeys, range) {
  let runStart = null;
  let previousValue = null;
  let best = null;
  const finishRun = (endIndex) => {
    if (runStart === null || endIndex <= runStart) return;
    const startPoint = rows[runStart];
    const endPoint = rows[endIndex];
    const duration = (endPoint.elapsedSeconds ?? endIndex) - (startPoint.elapsedSeconds ?? runStart);
    if (duration < range.flatlineSeconds) return;
    const startPeer = peerAverage(startPoint, peerKeys, key);
    const endPeer = peerAverage(endPoint, peerKeys, key);
    const peerMotion = Number.isFinite(startPeer) && Number.isFinite(endPeer) ? Math.abs(endPeer - startPeer) : null;
    if (!Number.isFinite(peerMotion) || peerMotion < range.flatlinePeerMotion) return;
    const candidate = {
      value: previousValue,
      duration,
      peerMotion,
      startTime: startPoint.elapsedSeconds ?? runStart,
      endTime: endPoint.elapsedSeconds ?? endIndex,
    };
    if (!best || candidate.duration > best.duration) best = candidate;
  };

  rows.forEach((point, index) => {
    const value = point[key];
    if (!Number.isFinite(value)) {
      finishRun(index - 1);
      runStart = null;
      previousValue = null;
      return;
    }
    if (value !== previousValue) {
      finishRun(index - 1);
      runStart = index;
      previousValue = value;
    }
  });
  finishRun(rows.length - 1);
  return best;
}

function sensorPointCondition(point, key, peerKeys, range) {
  const value = point?.[key];
  if (!Number.isFinite(value)) return null;
  const peer = peerAverage(point, peerKeys, key);
  const peerIsComparable = Number.isFinite(peer) && peer >= range.peerMinimum;
  if (value <= 0) {
    return { kind: "dropout", value, peer, delta: peerIsComparable ? value - peer : null };
  }
  if (value < range.min || value > range.max) {
    return { kind: "range", value, peer, delta: peerIsComparable ? value - peer : null };
  }
  if (!peerIsComparable) return null;
  const delta = value - peer;
  if (value <= peer - range.coldPeerDelta) return { kind: "coldPeer", value, peer, delta };
  if (value >= peer + range.hotPeerDelta) return { kind: "hotPeer", value, peer, delta };
  return null;
}

function sensorConditionReason(kind) {
  return {
    coldPeer: "cold versus peers",
    hotPeer: "hot versus peers",
    dropout: "dropout",
    range: "out of range",
    flatline: "flatline",
    jumps: "repeated abrupt jumps",
  }[kind] ?? "signal review";
}

function findSensorJumpWindows(jumpEvents, range) {
  const gapLimit = range.conditionGapSeconds ?? 12;
  const minimumDuration = range.conditionSeconds ?? 90;
  const minimumCount = range.jumpWindowMinimum ?? 6;
  const windows = [];
  let run = null;

  const finishRun = () => {
    if (!run) return;
    const duration = run.endTime - run.startTime;
    if (run.count >= minimumCount && duration >= minimumDuration) {
      windows.push({
        startTime: run.startTime,
        endTime: run.endTime,
        duration,
        kind: "jumps",
        reason: sensorConditionReason("jumps"),
        affectedCount: run.count,
        totalCount: run.count,
        maxDelta: run.maxDelta,
        label: "TIT signal quality review",
      });
    }
    run = null;
  };

  jumpEvents.forEach((event) => {
    if (run && event.time - run.endTime > gapLimit) finishRun();
    if (!run) {
      run = {
        startTime: event.time,
        endTime: event.time,
        count: 0,
        maxDelta: null,
      };
    }
    run.endTime = event.time;
    run.count += 1;
    run.maxDelta = Number.isFinite(run.maxDelta) ? Math.max(run.maxDelta, event.unexplainedDelta) : event.unexplainedDelta;
  });
  finishRun();
  return windows.sort((a, b) => b.duration - a.duration || b.affectedCount - a.affectedCount);
}

function findSensorConditionWindows(rows, key, peerKeys, range) {
  const minimumDuration = range.conditionSeconds ?? 90;
  const gapLimit = range.conditionGapSeconds ?? 12;
  const minimumAffectedRatio = range.conditionAffectedRatio ?? 0.55;
  const windows = [];
  let run = null;

  const finishRun = () => {
    if (!run) return;
    const duration = run.lastIssueTime - run.startTime;
    const affectedRatio = run.totalCount ? run.affectedCount / run.totalCount : 0;
    if (duration >= minimumDuration && affectedRatio >= minimumAffectedRatio) {
      const [kind] = Object.entries(run.kindCounts)
        .sort((a, b) => b[1] - a[1])[0] ?? ["signal"];
      windows.push({
        startTime: run.startTime,
        endTime: run.lastIssueTime,
        duration,
        kind,
        reason: sensorConditionReason(kind),
        affectedCount: run.affectedCount,
        totalCount: run.totalCount,
        affectedRatio,
        minDelta: run.minDelta,
        maxDelta: run.maxDelta,
      });
    }
    run = null;
  };

  const startRun = (point, index) => {
    const time = point.elapsedSeconds ?? index;
    run = {
      startTime: time,
      lastIssueTime: time,
      affectedCount: 0,
      totalCount: 0,
      kindCounts: {},
      minDelta: null,
      maxDelta: null,
    };
  };

  const addCondition = (point, index, condition) => {
    const time = point.elapsedSeconds ?? index;
    run.lastIssueTime = time;
    run.affectedCount += 1;
    run.totalCount += 1;
    run.kindCounts[condition.kind] = (run.kindCounts[condition.kind] ?? 0) + 1;
    if (Number.isFinite(condition.delta)) {
      run.minDelta = Number.isFinite(run.minDelta) ? Math.min(run.minDelta, condition.delta) : condition.delta;
      run.maxDelta = Number.isFinite(run.maxDelta) ? Math.max(run.maxDelta, condition.delta) : condition.delta;
    }
  };

  rows.forEach((point, index) => {
    const time = point.elapsedSeconds ?? index;
    const condition = sensorPointCondition(point, key, peerKeys, range);
    if (condition) {
      if (run && time - run.lastIssueTime > gapLimit) finishRun();
      if (!run) startRun(point, index);
      addCondition(point, index, condition);
      return;
    }
    if (!run) return;
    if (time - run.lastIssueTime > gapLimit) {
      finishRun();
      return;
    }
    run.totalCount += 1;
  });

  finishRun();
  return windows.sort((a, b) => b.duration - a.duration);
}

function turboProbeLabel(key, descriptors = []) {
  if ((descriptors?.length ?? 0) === 2) {
    const scoped = engineScopedChannelLabel(key, descriptors, { style: ENGINE_LABEL_STYLES.NARRATIVE });
    if (scoped) return scoped;
  }
  if (key === "tit1") return "TIT1";
  if (key === "tit2") return "TIT2";
  return "TIT";
}

function turboTwinLabels(descriptors = []) {
  const [primary, secondary] = descriptors ?? [];
  if (!primary || !secondary) return null;
  return {
    primaryLabel: primary.label,
    primaryShortLabel: primary.shortLabel,
    secondaryLabel: secondary.label,
    secondaryShortLabel: secondary.shortLabel,
  };
}

function summarizeSensorChannel(rows, key, peerKeys, { twin = false, descriptors = [] } = {}) {
  const type = sensorChannelType(key);
  const range = sensorRangeForType(type);
  let finiteCount = 0;
  let zeroCount = 0;
  let outOfRangeCount = 0;
  let coldMismatchCount = 0;
  let hotMismatchCount = 0;
  let impossibleJumpCount = 0;
  let peerComparableCount = 0;
  let previousPoint = null;
  let previousValue = null;
  const jumpEvents = [];
  let firstTime = null;
  let lastTime = null;

  rows.forEach((point, index) => {
    const value = point[key];
    if (!Number.isFinite(value)) return;
    finiteCount += 1;
    if (firstTime === null) firstTime = point.elapsedSeconds ?? index;
    lastTime = point.elapsedSeconds ?? index;
    if (value <= 0) zeroCount += 1;
    if (value < range.min || value > range.max) outOfRangeCount += 1;

    const peer = peerAverage(point, peerKeys, key);
    if (Number.isFinite(peer) && peer >= range.peerMinimum) {
      peerComparableCount += 1;
      if (value <= peer - range.coldPeerDelta) coldMismatchCount += 1;
      if (value >= peer + range.hotPeerDelta) hotMismatchCount += 1;
    }

    if (previousPoint && Number.isFinite(previousValue)) {
      const dt = Math.max(0.2, (point.elapsedSeconds ?? index) - (previousPoint.elapsedSeconds ?? index - 1));
      const delta = value - previousValue;
      const peerNow = peerAverage(point, peerKeys, key);
      const peerBefore = peerAverage(previousPoint, peerKeys, key);
      const peerDelta = Number.isFinite(peerNow) && Number.isFinite(peerBefore) ? peerNow - peerBefore : 0;
      const unexplainedDelta = Math.abs(delta - peerDelta);
      if (unexplainedDelta > Math.max(range.jumpFloor, dt * range.jumpRate)) {
        impossibleJumpCount += 1;
        jumpEvents.push({
          time: point.elapsedSeconds ?? index,
          delta,
          unexplainedDelta,
        });
      }
    }

    previousPoint = point;
    previousValue = value;
  });

  const totalRows = rows.length || 1;
  const missingRatio = Math.max(0, totalRows - finiteCount) / totalRows;
  const zeroRatio = finiteCount ? zeroCount / finiteCount : 0;
  const outOfRangeRatio = finiteCount ? outOfRangeCount / finiteCount : 0;
  const coldMismatchRatio = peerComparableCount ? coldMismatchCount / peerComparableCount : 0;
  const hotMismatchRatio = peerComparableCount ? hotMismatchCount / peerComparableCount : 0;
  const jumpRatio = finiteCount > 1 ? impossibleJumpCount / (finiteCount - 1) : 0;
  const flatline = findSensorFlatline(rows, key, peerKeys, range);
  const jumpWindows = findSensorJumpWindows(jumpEvents, range);
  const jumpWindow = jumpWindows[0] ?? null;
  const conditionWindows = [
    ...findSensorConditionWindows(rows, key, peerKeys, range),
    ...jumpWindows,
  ].sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0));
  const conditionWindow = conditionWindows[0] ?? null;
  const jumpConfig = turboParameters.titQuality.jumpSuspicion;
  const weights = turboParameters.titQuality.suspicionWeights;
  const jumpSuspicion = Math.min(
    jumpConfig.cap,
    impossibleJumpCount * jumpConfig.countWeight
      + jumpRatio * jumpConfig.ratioWeight
      + (jumpWindow ? Math.min(jumpConfig.windowCap, jumpConfig.windowBase + jumpWindow.duration / jumpConfig.windowDurationDivisor) : 0),
  );
  const suspicion = zeroRatio * weights.zeroRatio
    + outOfRangeRatio * weights.outOfRangeRatio
    + missingRatio * weights.missingRatio
    + coldMismatchRatio * weights.coldMismatchRatio
    + hotMismatchRatio * weights.hotMismatchRatio
    + jumpSuspicion
    + (conditionWindow ? Math.min(weights.conditionWindowCap, weights.conditionWindowBase + conditionWindow.duration / weights.conditionWindowDurationDivisor) : 0)
    + (flatline ? Math.min(weights.flatlineCap, weights.flatlineBase + flatline.duration / weights.flatlineDurationDivisor) : 0);

  return {
    key,
    label: turboProbeLabel(key, twin ? descriptors : []),
    type,
    finiteCount,
    missingCount: Math.max(0, totalRows - finiteCount),
    missingRatio,
    zeroCount,
    outOfRangeCount,
    coldMismatchCount,
    hotMismatchCount,
    peerComparableCount,
    coldMismatchRatio,
    hotMismatchRatio,
    impossibleJumpCount,
    jumpRatio,
    jumpEvents,
    jumpWindows,
    jumpWindow,
    flatline,
    conditionWindows,
    conditionWindow,
    firstTime,
    lastTime,
    suspicion,
  };
}

function summarizeTurboTitQuality(data, { twin = false, descriptors = [] } = {}) {
  const keys = scalarThermalProbeKeys(data);
  if (!keys.length) return null;
  const workRows = data.filter(engineWorkPoint);
  const rows = workRows.length >= turboParameters.titQuality.minWorkRows ? workRows : data;
  const peerKeysFor = (key) => (twin && sensorChannelType(key) === "tit" ? [] : sensorPeerKeysForKey(key, keys));
  const summaries = keys.map((key) => summarizeSensorChannel(rows, key, peerKeysFor(key), { twin, descriptors }))
    .sort((a, b) => b.suspicion - a.suspicion);
  const top = summaries[0] ?? null;
  if (!top) return null;
  const qualityConfig = turboParameters.titQuality;
  const penalty = top.suspicion >= qualityConfig.reviewSuspicion
    ? Math.min(qualityConfig.penaltyCap, top.suspicion * qualityConfig.penaltyScale)
    : 0;
  const window = top.conditionWindow
    ? {
        ...top.conditionWindow,
        label: `${top.label} signal quality review`,
      }
    : top.jumpWindow
      ? {
          ...top.jumpWindow,
          label: `${top.label} signal quality review`,
        }
      : null;
  const unstableSampleCount = Number.isFinite(window?.affectedCount)
    ? window.affectedCount
    : window && Number.isFinite(window.startTime) && Number.isFinite(window.endTime)
      ? rows.filter((point) => {
          const time = point.elapsedSeconds;
          return Number.isFinite(time)
            && time >= window.startTime
            && time <= window.endTime
            && Number.isFinite(point[top.key]);
        }).length
      : 0;
  return {
    key: top.key,
    label: top.label,
    score: clampScore(turboParameters.scoring.passScore - Math.min(qualityConfig.scoreSuspicionCap, top.suspicion)),
    suspicion: top.suspicion,
    penalty,
    impossibleJumpCount: top.impossibleJumpCount,
    jumpRatio: top.jumpRatio,
    maxJumpDelta: extentFromValues(top.jumpEvents.map((event) => event.unexplainedDelta))?.max ?? null,
    unstableSampleCount,
    zeroCount: top.zeroCount,
    outOfRangeCount: top.outOfRangeCount,
    missingRatio: top.missingRatio,
    window,
    summaries,
  };
}

export function analyzeTurboHealth(data, options = {}) {
  const profile = options.aircraftProfile ?? null;
  if (profileExplicitlyDisablesTurbo(profile)) {
    return {
      key: "turbo",
      score: null,
      note: "Turbo is not configured for this aircraft",
      details: {
        available: false,
        configured: false,
        inferred: false,
        disabledByProfile: true,
      },
    };
  }
  const configured = profileImpliesTurbo(profile);
  const inferred = hasTurboTelemetry(data);
  if (!configured && !inferred) {
    return { key: "turbo", score: null, note: "No turbo equipment configured or inferred", details: { available: false } };
  }

  const turboTrendData = engineRunningTrendRows(data);
  const titValues = turboTemperatureValues(turboTrendData);
  const mapValues = turboMapValues(turboTrendData);
  const upperDeckValues = finiteValues(turboTrendData, "upperDeckPressure");
  const compressorValues = finiteValues(data, "compressorDischargeTemp");
  const inductionValues = finiteValues(data, "inductionTemp");
  const maxMapInHg = turboProfileValue(profile, "maxMapInHg");
  const maxContinuousMapInHg = turboProfileValue(profile, "maxContinuousMapInHg");
  const maxTakeoffMapInHg = turboProfileValue(profile, "maxTakeoffMapInHg");
  const maxTakeoffDurationMinutes = turboProfileValue(profile, "maxTakeoffDurationMinutes");
  const maxTitF = turboProfileValue(profile, "maxTitF");
  const recommendedTitMarginF = turboProfileValue(profile, "recommendedTitMarginF");
  const maxCompressorDischargeTempF = turboProfileValue(profile, "maxCompressorDischargeTempF");
  const maxInductionTempF = turboProfileValue(profile, "maxInductionTempF");
  const peakTitPoint = turboPeakPoint(turboTrendData, "tit");
  const peakMapPoint = turboPeakPoint(turboTrendData, "map");
  const peakDeckPoint = turboPeakPoint(turboTrendData, "upperDeckPressure");
  const peakTit = extentFromValues(titValues)?.max ?? null;
  const p95Tit = quantile(titValues, 0.95);
  const peakMap = extentFromValues(mapValues)?.max ?? null;
  const p95Map = quantile(mapValues, 0.95);
  const peakUpperDeck = extentFromValues(upperDeckValues)?.max ?? null;
  const peakCompressor = extentFromValues(compressorValues)?.max ?? null;
  const peakInduction = extentFromValues(inductionValues)?.max ?? null;
  const peakTitLeft = extentFromValues(finiteValues(turboTrendData, "tit1"))?.max ?? null;
  const peakTitRight = extentFromValues(finiteValues(turboTrendData, "tit2"))?.max ?? null;
  const p95TitLeft = quantile(finiteValues(turboTrendData, "tit1"), 0.95);
  const p95TitRight = quantile(finiteValues(turboTrendData, "tit2"), 0.95);
  const peakMapLeft = extentFromValues(finiteValues(turboTrendData, "map1"))?.max ?? null;
  const peakMapRight = extentFromValues(finiteValues(turboTrendData, "map2"))?.max ?? null;
  const p95MapLeft = quantile(finiteValues(turboTrendData, "map1"), 0.95);
  const p95MapRight = quantile(finiteValues(turboTrendData, "map2"), 0.95);
  const missingContext = !mapValues.length && !titValues.length && !upperDeckValues.length && !compressorValues.length && !inductionValues.length;
  const descriptors = Array.isArray(options.engineDescriptors) && options.engineDescriptors.length
    ? options.engineDescriptors
    : engineDescriptorsForData(data, profile, options);
  const twin = descriptors.length === 2;
  const twinLabels = twin ? turboTwinLabels(descriptors) : null;
  const titQuality = summarizeTurboTitQuality(data, { twin, descriptors });
  const positiveLimit = (value) => (Number.isFinite(value) && value > 0 ? value : null);
  const limits = {
    maxMapInHg: positiveLimit(maxMapInHg),
    maxContinuousMapInHg: positiveLimit(maxContinuousMapInHg),
    maxTakeoffMapInHg: positiveLimit(maxTakeoffMapInHg),
    maxTakeoffDurationMinutes: positiveLimit(maxTakeoffDurationMinutes),
    maxTitF: positiveLimit(maxTitF),
    recommendedTitMarginF: Number.isFinite(recommendedTitMarginF) ? recommendedTitMarginF : null,
    maxCompressorDischargeTempF: positiveLimit(maxCompressorDischargeTempF),
    maxInductionTempF: positiveLimit(maxInductionTempF),
  };
  const limitCount = [
    limits.maxMapInHg ?? limits.maxContinuousMapInHg ?? limits.maxTakeoffMapInHg,
    limits.maxTitF,
    limits.recommendedTitMarginF,
    limits.maxCompressorDischargeTempF,
    limits.maxInductionTempF,
  ].filter(Number.isFinite).length;
  const topPoint = peakTitPoint ?? peakMapPoint ?? peakDeckPoint ?? turboContextPoint(data);
  const top = topPoint ? {
    elapsedSeconds: topPoint.elapsedSeconds,
    phase: topPoint.phase,
    tit: Number.isFinite(topPoint.tit) ? topPoint.tit : averageValues([topPoint.tit1, topPoint.tit2]),
    map: topPoint.map,
    upperDeckPressure: topPoint.upperDeckPressure,
    fuelFlow: topPoint.fuelFlow,
    rpm: topPoint.rpm,
    altitude: topPoint.altitude,
    mapPeakSourceValue: turboMapValueForPoint(topPoint),
  } : null;
  const details = {
    available: true,
    configured,
    inferred,
    twin,
    limitCount,
    limits,
    peakTit,
    p95Tit,
    peakMap,
    p95Map,
    peakUpperDeck,
    peakCompressor,
    peakInduction,
    peakTitLeft,
    peakTitRight,
    p95TitLeft,
    p95TitRight,
    peakMapLeft,
    peakMapRight,
    p95MapLeft,
    p95MapRight,
    titQuality,
    top,
    engineLabels: twinLabels,
  };

  if (missingContext) {
    return {
      key: "turbo",
      score: null,
      note: configured ? "Turbo profile configured; need MAP, TIT, or boost channels" : "Turbo data not available",
      details,
    };
  }

  if (!limitCount) {
    return {
      key: "turbo",
      score: null,
      note: "Turbo data present; profile limits needed",
      details: {
        ...details,
        status: "needs-profile-limits",
      },
    };
  }

  const firstTakeoffTime = data.find((point) => point.phase === "Takeoff" && Number.isFinite(point.elapsedSeconds))?.elapsedSeconds ?? null;
  const takeoffDurationSeconds = Number.isFinite(limits.maxTakeoffDurationMinutes)
    ? limits.maxTakeoffDurationMinutes * 60
    : null;
  const mapLimitForPoint = (point) => {
    const elapsed = point?.elapsedSeconds;
    const inTakeoffPhase = point?.phase === "Takeoff";
    const insideTakeoffWindow = Number.isFinite(firstTakeoffTime)
      && Number.isFinite(takeoffDurationSeconds)
      && Number.isFinite(elapsed)
      && elapsed - firstTakeoffTime <= takeoffDurationSeconds;
    const useTakeoffLimit = Number.isFinite(takeoffDurationSeconds)
      ? insideTakeoffWindow
      : inTakeoffPhase;
    if (useTakeoffLimit && Number.isFinite(limits.maxTakeoffMapInHg)) return limits.maxTakeoffMapInHg;
    if (Number.isFinite(limits.maxContinuousMapInHg)) return limits.maxContinuousMapInHg;
    return limits.maxMapInHg;
  };
  const mapLabelForWindow = ({ peak }) => {
    if (Number.isFinite(limits.maxContinuousMapInHg) && peak?.limit === limits.maxContinuousMapInHg) return "MAP over configured continuous limit";
    if (Number.isFinite(limits.maxTakeoffMapInHg) && peak?.limit === limits.maxTakeoffMapInHg) return "MAP over configured takeoff limit";
    return "MAP over configured limit";
  };
  const mapWindows = turboLimitWindows(data, "map", mapLimitForPoint, mapLabelForWindow);
  const titWindows = turboLimitWindows(data, "tit", limits.maxTitF, "TIT over configured limit");
  const compressorWindows = turboLimitWindows(data, "compressorDischargeTemp", limits.maxCompressorDischargeTempF, "Compressor temp over configured limit");
  const inductionWindows = turboLimitWindows(data, "inductionTemp", limits.maxInductionTempF, "Induction temp over configured limit");
  const limitWindows = [...mapWindows, ...titWindows, ...compressorWindows, ...inductionWindows];
  const hasLimitWindow = limitWindows.some(Boolean);
  const highPowerHeat = summarizeHighPowerHeat(data, { profile, limits, peakMap, mapLimitForPoint });
  const nearTitLimit = Number.isFinite(limits.maxTitF) && Number.isFinite(limits.recommendedTitMarginF) && Number.isFinite(p95Tit)
    ? p95Tit >= limits.maxTitF - limits.recommendedTitMarginF
    : false;
  const mapOvershoot = Math.max(0, ...mapWindows.map((window) => Number.isFinite(window.overshoot) ? window.overshoot : 0));
  const titOvershoot = Number.isFinite(peakTit) && Number.isFinite(limits.maxTitF) ? Math.max(0, peakTit - limits.maxTitF) : 0;
  const compressorOvershoot = Number.isFinite(peakCompressor) && Number.isFinite(limits.maxCompressorDischargeTempF) ? Math.max(0, peakCompressor - limits.maxCompressorDischargeTempF) : 0;
  const inductionOvershoot = Number.isFinite(peakInduction) && Number.isFinite(limits.maxInductionTempF) ? Math.max(0, peakInduction - limits.maxInductionTempF) : 0;
  const titQualityWindow = titQuality?.window
    ? {
        ...titQuality.window,
        label: titQuality.window.label ?? "TIT signal quality review",
      }
    : null;
  const scoring = turboParameters.scoring;
  const titQualityPenalty = titQualityWindow ? Math.min(turboParameters.titQuality.penaltyCap, titQuality?.penalty ?? 0) : 0;
  const longestMapDuration = Math.max(0, ...mapWindows.map((window) => window.duration ?? 0));
  const longestTitDuration = Math.max(0, ...titWindows.map((window) => window.duration ?? 0));
  const mapDurationPenalty = longestMapDuration >= scoring.durationReviewSeconds ? Math.min(scoring.durationPenaltyCap, longestMapDuration / scoring.durationDivisor) : 0;
  const titDurationPenalty = longestTitDuration >= scoring.durationReviewSeconds ? Math.min(scoring.durationPenaltyCap, longestTitDuration / scoring.durationDivisor) : 0;
  const highPowerHeatPenalty = !hasLimitWindow ? highPowerHeat?.penalty ?? 0 : 0;
  const penalty = Math.min(scoring.mapOvershootCap, mapOvershoot * scoring.mapOvershootWeight + mapDurationPenalty)
    + Math.min(scoring.titOvershootCap, titOvershoot * scoring.titOvershootWeight + titDurationPenalty)
    + Math.min(scoring.compressorOvershootCap, compressorOvershoot * scoring.compressorOvershootWeight)
    + Math.min(scoring.inductionOvershootCap, inductionOvershoot * scoring.inductionOvershootWeight)
    + (nearTitLimit ? scoring.nearTitLimitPenalty : 0)
    + titQualityPenalty
    + highPowerHeatPenalty;
  const score = clampScore(scoring.passScore - penalty);
  const highPowerHeatWindow = !hasLimitWindow && highPowerHeat?.window ? highPowerHeat.window : null;
  const eventWindows = [...limitWindows, highPowerHeatWindow, titQualityWindow].filter(Boolean);
  const note = eventWindows.length
    ? eventWindows.map((window) => window.label).join("; ")
    : nearTitLimit
      ? "TIT operating near configured margin"
      : "Turbo channels inside configured limits";
  const status = hasLimitWindow
    ? "limit-review"
    : titQualityWindow
      ? "data-quality-review"
      : highPowerHeat?.status === "fuel-flow-heat-review"
        ? "heat-margin-review"
        : highPowerHeat?.status === "heat-margin-watch"
          ? "heat-margin-watch"
          : nearTitLimit
            ? "margin-watch"
            : "pass";
  return {
    key: "turbo",
    score,
    note,
    details: {
      ...details,
      status,
      eventWindows,
      mapOvershoot,
      titOvershoot,
      compressorOvershoot,
      inductionOvershoot,
      nearTitLimit,
      titQualityPenalty,
      highPowerHeat,
      highPowerHeatPenalty,
    },
  };
}
